import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox, Label, TextInput } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { BusinessModelEnum, Course, CourseFormatEnum } from '@/models/Course';
import FormControls from './CourseFormControls';

export type CourseFormFields = {
  name: string;
  abbreviation: string;
  formats: CourseFormatEnum[];
  businessModels: BusinessModelEnum[];
};

export type CourseFormVariant = 'default' | 'inline';

type CourseFormProps = {
  course?: Course;
  isLoading?: boolean;
  onSubmit: (data: CourseFormFields) => void;
  onCancel?: () => void;
  variant?: CourseFormVariant;
};

const FORMAT_OPTIONS = [CourseFormatEnum.ONLINE, CourseFormatEnum.IN_PERSON];
const BUSINESS_MODEL_OPTIONS = [
  BusinessModelEnum.CNA_STANDARD,
  BusinessModelEnum.IN_SCHOOL,
  BusinessModelEnum.SELF_PACED,
];

export default function CourseForm({
  course,
  isLoading,
  onSubmit,
  onCancel,
  variant = 'default',
}: CourseFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseForm.form',
  });
  const { t: tFormats } = useTranslation('translation', {
    keyPrefix: 'courseFormats',
  });
  const { t: tModels } = useTranslation('translation', {
    keyPrefix: 'businessModels',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(
    (): CourseFormFields => ({
      name: course?.name ?? '',
      abbreviation: course?.abbreviation ?? '',
      formats: course?.formats ?? [],
      businessModels: course?.businessModels ?? [],
    }),
    [course],
  );

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CourseFormFields>({ defaultValues });

  useEffect(() => {
    const subscribe = watch(value => {
      setHasChanges(!isEqual(value, defaultValues));
    });
    return () => {
      setHasChanges(false);
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset label={t('name')} labelAlt={errors?.name?.message}>
        <TextInput
          type="text"
          placeholder={t('namePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('nameRequired') })}
        />
      </TextInput.Fieldset>

      <TextInput.Fieldset
        label={t('abbreviation')}
        labelAlt={errors?.abbreviation?.message}
      >
        <TextInput
          type="text"
          placeholder={t('abbreviationPlaceholder')}
          className="w-full"
          color={!errors?.abbreviation?.message ? 'default' : 'error'}
          {...register('abbreviation', {
            required: t('abbreviationRequired'),
          })}
        />
      </TextInput.Fieldset>
      <div className="flex justify-between items-start">
        <Fieldset
          label={t('format')}
          className="w-1/2"
          labelAlt={errors?.formats?.message}
        >
          <Checkbox.Group>
            {FORMAT_OPTIONS.map(format => (
              <Label key={format}>
                <Checkbox
                  value={format}
                  color="primary"
                  {...register('formats', {
                    required: t('formatRequired'),
                  })}
                />
                {tFormats(format)}
              </Label>
            ))}
          </Checkbox.Group>
        </Fieldset>

        <Fieldset
          label={t('businessModel')}
          className="w-1/2"
          labelAlt={errors?.businessModels?.message}
        >
          <Checkbox.Group>
            {BUSINESS_MODEL_OPTIONS.map(model => (
              <Label key={model}>
                <Checkbox
                  value={model}
                  color="primary"
                  {...register('businessModels', {
                    required: t('businessModelRequired'),
                  })}
                />
                {tModels(model)}
              </Label>
            ))}
          </Checkbox.Group>
        </Fieldset>
      </div>
      <FormControls
        variant={variant}
        isLoading={isLoading}
        onCancel={onCancel}
        hasChanges={hasChanges}
      />
    </form>
  );
}
